import * as React from 'react';
import { useSelector } from 'react-redux';
import { FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { CheckoutOrderFragment } from '@websky/graphql';
import { useTheme } from '../../../../theme';
import { getOrder } from '../../../store/order/selectors';
import { usePassengerConsents } from './PassengersConsents.hook';
import { useCustomerInfo } from '../FormBlockSlot/hooks';
import Checkbox from '../../../../BaseComponents/Checkbox';
import Tooltip from '../../../../BaseComponents/Tooltip';
import FareConditionsModal from '../../../../FareConditions/components/Component';
import { SafeHtml } from '@shared/ui';

interface Props {
	login?: string;
	customer?: CheckoutOrderFragment['customer'];
	isAuthorized?: boolean;
}

const PassengersConsents: React.FC<Props> = ({ login, customer, isAuthorized }) => {
	const { t } = useTranslation('PassengersConsents');
	const { PassengersConsents: theme } = useTheme('Checkout');

	const order = useSelector(getOrder);

	const orderId = order.id;
	const flightId = order.flight.id;
	const segmentId = order.flight.segments[0].segment.id;

	const { showSubscribedCheckbox } = useCustomerInfo({ login, customer, isAuthorized });
	const model = usePassengerConsents({ showSubscribedCheckbox });

	if (!model.isVisible) {
		return null;
	}

	return (
		<>
			<div className={theme.container} onClick={model.onClick}>
				<FormControlLabel
					classes={{
						root: theme.label,
						label: cn(theme.label, theme.label_main)
					}}
					label={<SafeHtml tag="span" html={t('Consents_Main')} />}
					control={
						<Tooltip
							title={model.termsField.meta.error}
							open={model.termsField.meta.submitFailed && !model.termsField.input.value}
						>
							<Checkbox checked={model.isChecked} indeterminate={model.isIndeterminate} />
						</Tooltip>
					}
					onChange={model.onToggleAll}
					ref={model.checkboxRef}
				/>
				{model.isVisible && (
					<div className={theme.conditions}>
						{model.conditionKeys.map(condition => (
							<FormControlLabel
								key={condition.key}
								classes={{ root: theme.label, label: theme.label }}
								control={
									<Checkbox
										checked={model.selectedKeys.includes(condition.key)}
										onChange={model.onToggle(condition.key)}
									/>
								}
								label={<SafeHtml tag="span" html={t(condition.key)} />}
							/>
						))}
					</div>
				)}
			</div>

			<FareConditionsModal
				orderId={orderId}
				flightId={flightId}
				segmentId={segmentId}
				isOpen={model.isOpenTermsOfTariff}
				onClose={model.onCloseTermsOfTariff}
			/>
		</>
	);
};

export default PassengersConsents;
